/** Reference box used to fit media when the viewport size is unknown (SSR). */
const kReferenceViewportWidth = 1280;
const kReferenceViewportHeight = 720;

/**
 * Fit a media aspect ratio (`width` × `height`) inside the current viewport,
 * downscaling to fit and never upscaling.
 *
 * `react-photo-view` fits IMAGE slides to the viewport itself, but a custom
 * `render` slide (e.g. a `<video>`) is drawn at the raw width/height passed to
 * `PhotoView`, at scale 1 — so oversized dimensions overflow and crop. Passing
 * viewport-fitted dimensions keeps the whole media visible.
 *
 * SSR-safe: falls back to a reference box when `window` is unavailable. The
 * result only feeds the client-only lightbox slider, never SSR'd DOM, so the
 * `window` read cannot cause a hydration mismatch.
 */
export function fitToViewport(
  width: number,
  height: number,
): { width: number; height: number } {
  const maxWidth =
    typeof window === 'undefined' ? kReferenceViewportWidth : window.innerWidth;
  const maxHeight =
    typeof window === 'undefined'
      ? kReferenceViewportHeight
      : window.innerHeight;
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}
